import { useRef } from 'react'
import { useVisemeEnvelope } from '../useVisemeEnvelope'
import { useVizCanvas } from '../useVizCanvas'
import { withAlpha } from '../vizColor'

const BARS = 28

// Per-viseme spectral TILT: where this phoneme puts its energy across the bar
// bank (0 = left/low bands .. 1 = right/high bands). Real speech spectra differ
// sharply by phoneme — open vowels are low/mid-heavy, sibilants/fricatives
// (s, S, f, T) are high-frequency, plosives are broadband transients — so keying
// the band profile off the live viseme makes the bars resemble actual speech
// rather than a uniform pulse. `center` is the energy centroid (0..1), `spread`
// how broadband it is (low = peaky, high = flat/broadband).
const VISEME_TILT = {
  sil: { center: 0.5, spread: 1.0 },
  p: { center: 0.5, spread: 1.0 }, // plosive burst — broadband
  t: { center: 0.62, spread: 0.9 }, // t/d/n — broadband, slightly high
  k: { center: 0.55, spread: 0.9 }, // k/g
  f: { center: 0.82, spread: 0.5 }, // f/v — high fricative
  T: { center: 0.85, spread: 0.45 }, // th — high
  s: { center: 0.92, spread: 0.35 }, // s/z — very high sibilant
  S: { center: 0.88, spread: 0.4 }, // sh/ch — high sibilant
  r: { center: 0.4, spread: 0.7 }, // r — low/mid
  i: { center: 0.6, spread: 0.55 }, // ee — mid w/ high formant
  e: { center: 0.45, spread: 0.6 }, // eh — low/mid
  '@': { center: 0.4, spread: 0.6 }, // schwa — low/mid
  a: { center: 0.3, spread: 0.7 }, // ah — low, fairly broad
  o: { center: 0.25, spread: 0.55 }, // oh — low, rounded
  u: { center: 0.2, spread: 0.5 }, // oo — lowest, peaky
}

// Stable per-bar pseudo-random offsets (a cheap hash, NOT Math.random so the
// texture is deterministic frame-to-frame). Used to seed each bar's noise phase
// so the bank looks irregular/jagged instead of a single smooth wave.
const SEEDS = Array.from({ length: BARS }, (_, i) => {
  const s = Math.sin(i * 12.9898 + 4.1414) * 43758.5453
  return s - Math.floor(s) // fract -> 0..1
})

// Spectrum-style equalizer bars — the production speech visualizer. We have one
// amplitude (not a real spectrum), so we synthesize a SPEECH-LIKE band profile:
// the current phoneme (viseme) tilts energy across the bank (VISEME_TILT), and
// layered value-noise per bar adds the irregular, jagged texture real speech
// has. Scaled by the live envelope; rounded tops + falling peak caps for the
// classic analyzer feel. Idle = gentle resting shimmer so the fixed-height frame
// stays alive at rest.
export function BarsViz({ character, color, anyVoice }) {
  const { read, activeRef, visemeRef, reduceMotion } = useVisemeEnvelope(character, { anyVoice })
  const valsRef = useRef(new Float32Array(BARS))
  const peaksRef = useRef(new Float32Array(BARS))

  const draw = (ctx, { w, h, amp, active, t }) => {
    const vals = valsRef.current
    const peaks = peaksRef.current
    const gap = 2
    const bw = (w - gap * (BARS - 1)) / BARS
    const radius = Math.min(bw / 2, 3)
    const maxBar = h * 0.94 // headroom so tall bars don't clip the top edge
    // Low idle floor so resting bars shimmer faintly instead of going dead-flat.
    const idle = active ? 0 : 0.05

    // The phoneme being spoken shapes WHERE the energy sits this frame.
    const tilt = VISEME_TILT[visemeRef.current] || VISEME_TILT.sil

    for (let i = 0; i < BARS; i++) {
      const pos = i / (BARS - 1) // 0..1 across the bank (low -> high band)
      const seed = SEEDS[i]
      // Phoneme-shaped band envelope: a bump centred on the phoneme's spectral
      // centroid, width set by its spread. This is what makes vowels light up
      // the low/mid bars and sibilants light up the high bars.
      const d = (pos - tilt.center) / tilt.spread
      const band = Math.exp(-d * d * 2.2)
      // Layered value-noise: three sines at different rates + per-bar seed phase,
      // so each bar flickers on its own schedule — the irregular, speech-like
      // texture. Fast term gives syllable-rate sparkle; slow term gives drift.
      const n =
        0.45 * Math.sin(t * 0.9 + seed * 31.4) +
        0.30 * Math.sin(t * 2.1 + seed * 17.7 + i) +
        0.25 * Math.sin(t * 4.3 + seed * 53.1 + i * 2.0)
      const noise = 0.5 + 0.5 * n // 0..1
      // Combine: phoneme band shape, modulated by per-bar noise. Keep a floor so
      // active bars never fully die between flickers (reads as energy, not gaps).
      const profile = band * (0.35 + 0.65 * noise)
      const driven = amp * (0.25 + 0.95 * profile)
      const rest = idle * (0.4 + 0.6 * noise) * (0.5 + 0.5 * band)
      const target = Math.max(0, Math.min(1, Math.max(driven, rest)))
      // Smooth toward target — fast attack (snappy syllable transients), slower
      // release so peaks linger briefly like real speech energy.
      vals[i] += (target - vals[i]) * (target > vals[i] ? 0.6 : 0.22)
      // Peak cap falls slowly; only shown when actually speaking.
      peaks[i] = Math.max(vals[i], peaks[i] - 0.012)

      // Keep a 2px sliver visible even at zero so the bank reads as bars.
      const bh = Math.max(2, vals[i] * maxBar)
      const x = i * (bw + gap)
      const y = h - bh
      const grad = ctx.createLinearGradient(0, h, 0, y)
      grad.addColorStop(0, withAlpha(color, 0.3))
      grad.addColorStop(1, color)
      ctx.fillStyle = grad
      ctx.shadowColor = color
      ctx.shadowBlur = 6
      roundedTopRect(ctx, x, y, bw, bh, radius)
      ctx.fill()
      ctx.shadowBlur = 0

      // Peak cap (only while speaking — it reads as noise when idle).
      if (active && peaks[i] > 0.03) {
        const py = h - peaks[i] * maxBar
        ctx.fillStyle = color
        ctx.fillRect(x, Math.max(0, py - 2), bw, 2)
      }
    }
  }

  const ref = useVizCanvas({ read, activeRef, reduceMotion, draw })
  return <canvas ref={ref} style={{ display: 'block', width: '100%', height: '100%' }} />
}

// A rectangle with rounded top corners (bottom sits flush on the baseline).
function roundedTopRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h)
  ctx.beginPath()
  ctx.moveTo(x, y + h)
  ctx.lineTo(x, y + rr)
  ctx.quadraticCurveTo(x, y, x + rr, y)
  ctx.lineTo(x + w - rr, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr)
  ctx.lineTo(x + w, y + h)
  ctx.closePath()
}
